import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function OndtIKaebenPage() {
  const jawSymptoms = [
    {
      title: "Ømhed i kæben",
      desc: "Især ved tygning af mad eller når du vågner med ømme, overbelastede muskler.",
      icon: "fa-solid fa-utensils",
    },
    {
      title: "Træthed i tyggemusklerne",
      desc: "Fornemmelse af udmattede muskler i kinderne efter tale, arbejde eller koncentration.",
      icon: "fa-solid fa-face-grimace",
    },
    {
      title: "Kliklyde ledsaget af ømhed",
      desc: "Hørbare klik- eller smældlyde i kæbeleddet, når du åbner munden, tygger eller gaber.",
      icon: "fa-solid fa-volume-high",
    },
    {
      title: "Nedsat bevægelighed",
      desc: "Besvær eller stivhed, når du skal gabe op, spise fast føde eller bevæge kæben til siden.",
      icon: "fa-solid fa-arrows-up-down",
    },
  ];

  const combinationMethod = [
    {
      title: "Specialiseret manuel kæbebehandling",
      desc: "Målrettet efteruddannelse i manuel kæbebehandling. Med blide, præcise tryk afspændes de dybe tyggemuskler udvendigt på kinden og nænsomt indvendigt i munden med sterile handsker.",
    },
    {
      title: "Japansk akupunktur med ultra-tynde nåle",
      desc: "Der anvendes ultra-fine, sterile engangsnåle eller ikke-penetrerende kontaktnåle, der genopretter blodtilførslen og dæmper overaktive nervesignaler helt uden ubehag.",
    },
    {
      title: "Afspænding af nakke og nervesystem",
      desc: "Kæben behandles i samspil med hele kroppen. Meridianpunkter i nakke, hænder og fødder beroliger nervesystemet og mindsker det generelle spændingsmønster.",
    },
    {
      title: "Vejledning i hverdagsaflastning",
      desc: "Konkrete råd til kæbens hvileposition i hverdagen, justering af spisevaner samt enkle vaner, der forhindrer ubevidst tandpresning.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Akupunktur mod Kæbesmerter | Risskov Klinik</title>
        <meta
          name="description"
          content="Lider du af kæbesmerter, tandpres eller spændinger i kæbeleddet? Oplev mærkbar lindring med blid japansk akupunktur hos Kita i Risskov."
        />
        <meta
          property="og:title"
          content="Akupunktur mod Kæbesmerter | Risskov Klinik"
        />
        <meta
          property="og:description"
          content="Lider du af kæbesmerter, tandpres eller spændinger i kæbeleddet? Oplev mærkbar lindring med blid japansk akupunktur hos Kita i Risskov."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/ondt-i-kaeben" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Akupunktur ved kæbespændinger og ondt i kæben i Risskov",
            "url": "https://kita-akupunktur.dk/ondt-i-kaeben",
            "description":
              "Individuelt tilpasset akupunktur og kæbebehandling ved kæbespændinger, tandpresning og ondt i kæben i Risskov nær Aarhus.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={1} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li>
                <Link to="/japansk-akupunktur" className="hover:text-[#d74814] transition-colors">
                  Japansk akupunktur
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Kæbesmerter & spændinger
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>KÆBESPÆNDINGER & ONDT I KÆBEN • RISSKOV VED AARHUS</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] max-w-4xl leading-tight">
            Akupunktur ved kæbespændinger & ondt i kæben i Risskov
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] mt-4 max-w-3xl leading-relaxed">
            Har du ondt i kæben, når du tygger, gaber eller vågner om morgenen? Hos Kita Akupunktur i Risskov ved Aarhus tilbyder jeg individuelt tilpasset akupunktur ved kæbespændinger, også til dig fra Jystrup.
          </p>

          {/* Quick Info Badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
            >
              <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
              <span>Bestil tid til kæbebehandling</span>
            </Link>

            <a
              href="tel:+4529387186"
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white/90 text-[#593427] text-sm font-medium hover:bg-white border border-[#ffe0cf] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
              <span>Tlf. 29 38 71 86</span>
            </a>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427]">
              <i className="fa-solid fa-square-parking text-[#593427]" aria-hidden="true" />
              <span>Gratis parkering ved klinikken</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-14 sm:py-20 bg-[#fffaf7] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* SEO Intro Box: Ondt i kæben, akupunktur ved kæbespændinger i Risskov for dig fra Jystrup */}
          <div className="bg-white/90 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-[0_8px_30px_rgba(89,52,39,0.04)] relative overflow-hidden">
            <div className="max-w-4xl space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>INDIVIDUELT TILPASSET FORLØB</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#593427] leading-snug">
                Ondt i kæben, akupunktur ved kæbespændinger i Risskov for dig fra Jystrup
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                <p>
                  Har du ondt i kæben, når du tygger, gaber eller vågner om morgenen? Hos{" "}
                  <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
                    Kita Akupunktur
                  </Link>{" "}
                  i Risskov ved Aarhus tilbyder jeg individuelt tilpasset akupunktur ved kæbespændinger, også til dig fra Jystrup. Vi tager udgangspunkt i dine gener og din hverdag, så du ved, hvad et forløb indebærer, inden du vælger at begynde.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Hvad kan ligge bag dine kæbeproblemer? */}
          <div className="space-y-8">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>ÅRSAGER & SYMPTOMER</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Hvad kan ligge bag dine kæbeproblemer?
              </h2>
              <p className="text-base sm:text-lg text-[#7a5b4f] leading-relaxed">
                Kæbeproblemer kan hænge sammen med spændte tyggemuskler, tandpres eller tænderskæren. Smerterne kan også komme fra tænder eller kæbeled og bør derfor ikke automatisk tilskrives muskelspændinger. Det er vigtigt at få afklaret årsagen, især hvis generne er nye eller bliver værre. Du kan blandt andet opleve følgende gener:
              </p>
            </div>

            {/* 4 Symptoms Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {jawSymptoms.map((sym, idx) => (
                <div
                  key={idx}
                  className="bg-white/95 p-6 rounded-2xl border border-[#ffe0cf] hover:border-[#d74814]/40 hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="w-11 h-11 rounded-xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                      <i className={`${sym.icon} text-lg`} aria-hidden="true" />
                    </div>
                    <h3 className="font-serif font-semibold text-base text-[#593427]">
                      {sym.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                      {sym.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Gener omkring tindinger, nakke og skulder */}
            <div className="bg-[#fff5ee] p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] space-y-3">
              <div className="flex items-start space-x-3">
                <i className="fa-solid fa-circle-nodes text-[#d74814] text-xl mt-1 shrink-0" aria-hidden="true" />
                <div className="space-y-3">
                  <h3 className="font-serif font-semibold text-lg text-[#593427]">
                    Spændinger der forplanter sig
                  </h3>
                  <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                    Spændinger omkring kæben kan optræde sammen med ømhed i tindingerne, hovedpine eller spændinger i nakke og skulder. Fortæl mig gerne om alle dine gener, også dem der ikke sidder i kæben. Det hjælper os med at få et bedre overblik over din situation.
                  </p>
                  <div>
                    <Link
                      to="/spaendinger"
                      className="inline-flex items-center space-x-2 text-xs sm:text-sm font-semibold text-[#d74814] hover:underline"
                    >
                      <span>Læs mere om mit tilbud ved spændinger i nakke og skulder</span>
                      <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Ondt i kæben og stress, er der en sammenhæng? */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8 space-y-4">
                <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                  <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                  <span>KROP & STRESS</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                  Ondt i kæben og stress, er der en sammenhæng?
                </h2>
                <div className="space-y-3 text-[#7a5b4f] leading-relaxed text-base sm:text-lg">
                  <p>
                    Når du er presset, kan du ubevidst bide tænderne sammen. Derfor kan ondt i kæben og stress hænge sammen, uden at stress er hele forklaringen.
                  </p>
                  <p>
                    Vi taler om søvn, pauser og tidspunkter, hvor du spænder. Du kan også læse om mit tilbud ved stress og uro.
                  </p>
                </div>
                <div className="pt-2">
                  <Link
                    to="/stress"
                    className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-[#fff5ee] text-[#d74814] font-medium text-sm border border-[#ffe0cf] hover:bg-[#ffe0cf]/50 transition-colors"
                  >
                    <i className="fa-solid fa-spa text-xs" aria-hidden="true" />
                    <span>Læs om mit tilbud ved stress og uro</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-4 bg-[#fffaf7] p-6 rounded-2xl border border-[#ffe0cf] space-y-3 text-center sm:text-left">
                <div className="w-12 h-12 rounded-2xl bg-[#ffe0cf]/50 border border-[#ffe0cf] flex items-center justify-center text-[#d74814] mx-auto sm:mx-0">
                  <i className="fa-solid fa-brain text-xl" aria-hidden="true" />
                </div>
                <h3 className="font-serif font-semibold text-base text-[#593427]">
                  Ubevidst tandpresning
                </h3>
                <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                  Tyggemusklerne kan udøve et tryk på over 70 kg. Ved langvarig mental spænding spændes musklerne konstant – selv når du tror, du slapper af.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Akupunktur mod kæbespændinger i Aarhus for dig fra Jystrup */}
          <div className="glass-sakura p-8 sm:p-12 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>BEHANDLINGSTILGANG</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Akupunktur mod kæbespændinger i Aarhus for dig fra Jystrup
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-white/95 p-6 sm:p-8 rounded-2xl border border-[#ffe0cf] space-y-4">
                <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2">
                  <i className="fa-solid fa-user-doctor text-[#d74814]" aria-hidden="true" />
                  <span>Japansk akupunktur & individuel vurdering</span>
                </h3>
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Jeg tilbyder akupunktur mod kæbespændinger i Risskov ved Aarhus, også til dig fra Jystrup. Jeg arbejder med japansk akupunktur, hvor samtalen og en individuel vurdering indgår i valget af punkter. Dit forløb tager hensyn til, hvor du har ondt, hvor længe det har stået på, og hvordan du reagerer på behandlingen.
                </p>
              </div>

              <div className="bg-white/95 p-6 sm:p-8 rounded-2xl border border-[#ffe0cf] space-y-4">
                <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2">
                  <i className="fa-solid fa-scale-balanced text-[#d74814]" aria-hidden="true" />
                  <span>Et trygt supplement til læge og tandlæge</span>
                </h3>
                <p className="text-sm sm:text-base text-[#7a5b4f] leading-relaxed">
                  Akupunktur mod smerter tilbydes som et supplement, ikke som en erstatning for nødvendig behandling hos tandlæge eller læge. Der er usikkerhed om effekten ved kæbespændinger. Vi følger derfor, om du oplever en relevant ændring i smerter og funktion, og tager løbende stilling til, om forløbet giver mening for dig.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Hvordan føles japansk akupunktur? */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>NÆNSOMMET & TRYGHED</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Hvordan føles japansk akupunktur?
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base">
                <p>
                  Ved japansk akupunktur bruger jeg meget tynde, sterile engangsnåle. Indstikket er ofte overfladisk, og jeg tilpasser teknikken efter din sensitivitet. Hvis du er utryg ved nåle, taler vi om det før behandlingen. Der er også mulighed for kontaktnåle, som berører huden uden at trænge ind.
                </p>
                <p>
                  Du må gerne sige til, hvis noget føles ubehageligt, så jeg kan justere eller stoppe behandlingen. Nålene sidder typisk i 15 til 25 minutter, men det er ikke hele konsultationens varighed. Jeg forklarer undervejs, hvad der skal ske, og du behøver ikke have erfaring med akupunktur på forhånd.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 bg-white/95 p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-4">
              <h3 className="font-serif font-semibold text-lg text-[#593427] flex items-center space-x-2 border-b border-[#ffe0cf]/80 pb-3">
                <SakuraFlower className="w-4 h-4 text-[#d74814]" />
                <span>Trygge rammer i klinikken</span>
              </h3>
              <ul className="space-y-3.5 text-sm text-[#7a5b4f]">
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Ultra-tynde, sterile engangsnåle (blidt overfladisk indstik)</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Mulighed for ikke-penetrerende kontaktnåle (Teishin)</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Nålene sidder typisk i 15 til 25 minutter i rolige omgivelser</span>
                </li>
                <li className="flex items-start space-x-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d74814] mt-2 shrink-0" />
                  <span>Løbende dialog – du kan til enhver tid sige til undervejs</span>
                </li>
              </ul>
              <div className="pt-2">
                <div className="p-3.5 rounded-xl bg-[#ffe0cf]/30 border border-[#ffe0cf] text-xs text-[#593427] flex items-center space-x-3">
                  <JapaneseBadge className="shrink-0" />
                  <span>RAB-godkendt: Mulighed for tilskud fra Sygeforsikringen "danmark" og sundhedsforsikringer.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Hvad kan du selv gøre, og hvornår skal du søge hjælp? */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Hvad kan du selv gøre */}
            <div className="lg:col-span-7 bg-white/95 p-7 sm:p-9 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>EGENOMSORG & HVERDAGSRÅD</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Hvad kan du selv gøre i hverdagen?
              </h2>
              <div className="space-y-4 text-[#7a5b4f] leading-relaxed text-base">
                <p>
                  I hverdagen kan du lægge mærke til, om du presser tænderne sammen under arbejde eller koncentration. Lad gerne tænderne være let adskilte, når kæben er i hvile.
                </p>
                <p>
                  Skær midlertidigt ned på tyggegummi og meget sej mad, hvis det provokerer smerterne, og undgå at tvinge kæben gennem smertefulde bevægelser.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-4 rounded-xl bg-[#fffaf7] border border-[#ffe0cf] space-y-1">
                  <div className="font-semibold text-xs text-[#d74814] uppercase tracking-wide">
                    Hvileposition
                  </div>
                  <p className="text-xs text-[#7a5b4f]">
                    Læber samlet, tænder adskilte og tungen hvilende let i ganen.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-[#fffaf7] border border-[#ffe0cf] space-y-1">
                  <div className="font-semibold text-xs text-[#d74814] uppercase tracking-wide">
                    Skån tyggemusklerne
                  </div>
                  <p className="text-xs text-[#7a5b4f]">
                    Undgå sejt kød, hårde skorper og tyggegummi ved akutte smerter.
                  </p>
                </div>
              </div>
            </div>

            {/* Hvornår skal du søge hjælp? */}
            <div className="lg:col-span-5 bg-[#fff5ee] p-7 sm:p-8 rounded-3xl border-2 border-[#d74814]/30 shadow-sm space-y-4">
              <div className="flex items-center space-x-2.5 text-[#d74814]">
                <i className="fa-solid fa-triangle-exclamation text-xl" aria-hidden="true" />
                <h3 className="font-serif font-bold text-lg text-[#593427]">
                  Hvornår skal du søge læge eller tandlæge?
                </h3>
              </div>
              <p className="text-sm text-[#7a5b4f] leading-relaxed">
                Kontakt tandlæge eller læge ved vedvarende smerter eller problemer med at åbne munden.
              </p>
              <p className="text-sm text-[#7a5b4f] leading-relaxed">
                Ved en låst kæbe, pludselig kraftig smerte, hævelse eller feber bør du søge hurtig vurdering. Akupunktur er ikke det rette første skridt i den situation og skal ikke forsinke en undersøgelse af tænder eller kæbeled.
              </p>
            </div>
          </div>

          {/* Section: Akupunktur i Risskov, et personligt forløb for dig fra Jystrup */}
          <div className="bg-white/95 p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-3xl space-y-3">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>FORLØBET HOS KITA AKUPUNKTUR</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427] leading-snug">
                Akupunktur i Risskov, et personligt forløb for dig fra Jystrup
              </h2>
              <p className="text-base sm:text-lg text-[#7a5b4f] leading-relaxed">
                Jeg hedder Mayumi Kita Thiele og er registreret som alternativ behandler, RAB.{" "}
                <Link
                  to="/om-mig"
                  className="text-[#d74814] font-medium underline hover:text-[#b83b0e] transition-colors"
                >
                  Du kan læse mere om mig og min baggrund her
                </Link>
                . Ved første besøg taler vi om dine symptomer, tidligere undersøgelser og dine ønsker. Ved opfølgning ser vi på, hvad der har ændret sig. Der er ikke et fast antal behandlinger, som passer til alle med kæbespændinger.
              </p>
            </div>

            {/* Klinik Info Card */}
            <div className="p-6 sm:p-8 rounded-2xl bg-[#fffaf7] border border-[#ffe0cf] space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="font-serif font-semibold text-lg text-[#593427]">
                    Kita Akupunktur i Risskov
                  </div>
                  <p className="text-sm text-[#7a5b4f]">
                    Kantorparken 11, 8240 Risskov • Gratis parkering lige ved døren
                  </p>
                  <p className="text-xs text-[#7a5b4f]">
                    Klinikken byder velkommen til klienter fra bl.a. Risskov, Aarhus og Jystrup.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href="tel:+4529387186"
                    className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white text-[#593427] text-sm font-medium border border-[#ffe0cf] hover:border-[#d74814] transition-colors"
                  >
                    <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
                    <span>29 38 71 86</span>
                  </a>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-2xs"
                  >
                    <span>Kontakt mig om en tid</span>
                  </Link>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-[#7a5b4f] pt-2 border-t border-[#ffe0cf]/80">
                Du finder Kita Akupunktur på Kantorparken 11, 8240 Risskov, med gratis parkering ved klinikken. Du er også velkommen fra Jystrup. Har du spørgsmål, eller vil du bestille en tid, er du velkommen til at ringe på 29 38 71 86. Du kan også kontakte mig her, så vi kan tale om dine gener og muligheder.
              </p>
            </div>
          </div>

          {/* Combination Approach Cards */}
          <div className="bg-white/95 p-8 sm:p-12 rounded-3xl border border-[#ffe0cf] shadow-sm space-y-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-2 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
                <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
                <span>UNIK KOMBINATIONSBEHANDLING</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Sådan behandler jeg kæbespændinger hos Kita Akupunktur
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f] mt-2">
                En nænsom og effektiv synergi mellem specialiseret manuel teknik og japansk akupunktur.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {combinationMethod.map((item, i) => (
                <div
                  key={i}
                  className="bg-[#fffaf7] p-6 rounded-2xl border border-[#ffe0cf] space-y-2.5"
                >
                  <h3 className="font-serif font-semibold text-base text-[#593427] flex items-center space-x-2">
                    <SakuraFlower className="w-4 h-4 text-[#d74814]" />
                    <span>{item.title}</span>
                  </h3>
                  <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Related pages links */}
          <div className="p-6 rounded-2xl bg-[#fff5ee] border border-[#ffe0cf] flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="space-y-1 text-center sm:text-left">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
                Relaterede behandlingsområder
              </div>
              <p className="text-sm text-[#593427] font-serif">
                Kæben er tæt knyttet til migræne, stress og muskelspændinger i nakken
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <Link
                to="/migraene"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Migræne & hovedpine →
              </Link>
              <Link
                to="/spaendinger"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Spændinger i nakken →
              </Link>
              <Link
                to="/stress"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Stress & uro →
              </Link>
              <Link
                to="/japansk-akupunktur"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Om japansk akupunktur →
              </Link>
              <Link
                to="/led-og-muskelsmerter"
                className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
              >
                Led- & muskelsmerter →
              </Link>
            </div>
          </div>

          {/* Hurtige genveje */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser & tilskud fra “danmark”
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumis kursus i kæbebehandling
            </Link>
            <Link
              to="/faq"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Samlet FAQ
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid i Risskov
            </Link>
          </div>

          {/* Contact and Booking Section */}
          <div className="pt-6">
            <div className="text-center max-w-2xl mx-auto mb-8 space-y-2">
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Bestil tid til kæbebehandling i Risskov
              </h2>
              <p className="text-sm sm:text-base text-[#7a5b4f]">
                Klinikken ligger i trygge og fredelige rammer på Kantorparken 11 i Risskov nær Aarhus med gratis parkering lige uden for døren.
              </p>
            </div>
            <ContactForm />
          </div>

          {/* FAQ Section sidst på siden */}
          <div className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
            <FaqSection
              title="Ofte stillede spørgsmål om kæbespændinger og tandpres"
              subtitle="Få svar på hvordan skånsom akupunktur løsner overbelastede tyggemuskler, afhjælper tænderskæren, kliklyde og smerter i kæbeleddet."
              faqs={[
                {
                  question: "Hvorfor opstår kæbespændinger, tandpres og kliklyde i kæbeleddet?",
                  answer: (
                    <>
                      Kæbespændinger skyldes hyppigt ubevidst tandpres eller tænderskæren (bruksisme) under søvn eller ved mental{" "}
                      <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        stress
                      </Link>{" "}
                      og høj koncentration. Tyggemusklen (masseter) er kroppens stærkeste muskel målt på kraft, og vedvarende overbelastning skaber dybe myoser, der kan forskyde kæbeleddets fine bruskskive. Dette fører til kliklyde, nedsat gabehøjde og smerter, som stråler til ører og tindinger (
                      <Link to="/migraene" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        hovedpine
                      </Link>
                      ).
                    </>
                  ),
                },
                {
                  question: "Hvordan hjælper akupunktur med at afspænde kæbemusklerne og kæbeleddet?",
                  answer:
                    "Ved at placere hårfine akupunkturnåle i og omkring tyggemusklerne samt på relaterede meridianpunkter i nakken og på hænderne udløses en direkte afspænding i det overspændte muskelvæv. Nålene øger den lokale mikrocirkulation dramatisk, så mælkesyre udrenses, og dæmper irritationen i kæbeleddets nerveforsyning. Dette giver kæben mulighed for at slappe af og bevæge sig frit igen.",
                },
                {
                  question: "Kan akupunktur hjælpe, hvis min bideskinne fra tandlægen ikke fjerner smerterne?",
                  answer: (
                    <>
                      Ja, det er en meget udbredt problematik. En bideskinne beskytter tandemaljen mod fysisk slid, men den fjerner desværre ikke den underliggende muskulære spænding eller nervesystemets trang til at bide hårdt sammen. Akupunktur behandler den egentlige rod til problemet ved at nulstille spændingstilstanden i tyggemusklerne og berolige det autonome nervesystem. Læs mere om{" "}
                      <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        stressreduktion
                      </Link>
                      .
                    </>
                  ),
                },
                {
                  question: "Hvor hurtigt mærkes effekten på kæben, og hvor mange sessioner behøves?",
                  answer: (
                    <>
                      Mange klienter oplever allerede efter første eller anden session en markant lettelse, en følelse af at 'kæben falder på plads', og at det bliver meget lettere at gabe uden modstand eller ømhed. For et holdbart og blivende resultat ved kronisk tandpres anbefales typisk et forløb på 4-6 ugentlige sessioner, hvor vi også afbalancerer{" "}
                      <Link to="/spaendinger" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        nakkespændinger
                      </Link>
                      . Som RAB-behandler gives der tilskud fra Sygeforsikring “danmark” (se{" "}
                      <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        priser og tilskud
                      </Link>
                      ).
                    </>
                  ),
                },
                {
                  question: "Er det smertefuldt at få akupunkturnåle sat i kæben og kinderne?",
                  answer: (
                    <>
                      Slet ikke. I ansigts- og kæberegionen anvender jeg specielle, ultratynde{" "}
                      <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                        japanske nåle
                      </Link>
                      , som indføres med største nænsomhed og præcision. De fleste mærker knap nok nålen, men oplever i stedet en dyb og behagelig forløsning i de fastlåste muskler, mens de hviler på briksen. Er du særligt nålefølsom, tilpasses teknikken, så du føler dig fuldstændig tryg.
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>
    </>
  );
}
